/**
 * Ky client setup — GENERATED ONCE, will NOT be overwritten on subsequent runs.
 * Re-generate intentionally with: swaggie ... --clientSetup <path> --forceSetup
 *
 * This file configures the ky HTTP client used by the generated API client.
 * Because ky requires hooks to be provided at creation time, this file exports
 * a `createKyConfig()` function that is called by `initKyHttp()` in api.ts.
 *
 * Usage in your app (see ZeusClientSetupProvider):
 *   import { setupZeusInterceptors } from './setup';
 *   import { initKyHttp } from './api';
 *
 *   useEffect(() => {
 *     const cleanup = setupZeusInterceptors(authRef);
 *     initKyHttp();
 *     return cleanup;
 *   }, []);
 */
import { captureModuleException, Module } from '@modules/shared/utils/captureModuleException';

import { StatusCodes } from 'http-status-codes';
import { isHTTPError } from 'ky';
import { testScenarioCookieName, testScenarioHeaderName } from 'shared/src/__tests__/common/testFixtures';
import { CRIBL_AGENT_HEADER_NAME, MAESTRO_AGENT } from 'shared/src/observability/constants';

import type { CommonAuthProviderContextValue } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import type { Options as KyOptions } from 'ky';
import type { RefObject } from 'react';

export type AuthRef = RefObject<Pick<CommonAuthProviderContextValue, 'getAccessToken' | 'loginWithRedirect'>>;

let zeusAuthRef: AuthRef | null = null;

function inferModuleFromUrl(url: string | undefined): Module {
  if (url?.includes('/billing')) {
    return Module.FINOPS;
  }

  if (url?.includes('/sandboxes')) {
    return Module.SANDBOXES;
  }

  if (url?.includes('/notifications')) {
    return Module.NOTIFICATIONS;
  }

  return Module.FINOPS;
}

function readTestScenarioCookie(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const row = document.cookie.split('; ').find((r) => r.startsWith(`${testScenarioCookieName}=`));

  return row?.split('=')[1];
}

function urlForCapture(requestUrl: string): string {
  try {
    return new URL(requestUrl).pathname;
  } catch {
    return requestUrl;
  }
}

/**
 * Binds auth to ky hooks (via module ref). Call before `initKyHttp()`.
 * Returns cleanup that clears the ref (used on unmount).
 */
export function setupZeusInterceptors(authRef: AuthRef): () => void {
  zeusAuthRef = authRef;

  return () => {
    zeusAuthRef = null;
  };
}

export type KySetupConfig = Pick<KyOptions, 'hooks' | 'prefix' | 'retry' | 'timeout'>;

/**
 * Returns the ky configuration used to create the HTTP client instance.
 * Called once by `initKyHttp()` in the generated api.ts.
 */
export function createKyConfig(): KySetupConfig {
  return {
    prefix: process.env.ZEUS_API_URL ?? '',
    hooks: {
      beforeRequest: [
        async ({ request }) => {
          request.headers.set(CRIBL_AGENT_HEADER_NAME, MAESTRO_AGENT);

          const testScenario = readTestScenarioCookie();

          if (testScenario) {
            request.headers.set(testScenarioHeaderName, testScenario);
          }

          const ref = zeusAuthRef?.current;

          if (!ref) {
            return;
          }

          const token = await ref.getAccessToken();

          if (token) {
            request.headers.set('Authorization', `Bearer ${token}`);
          }
        },
      ],
      beforeError: [
        async ({ error, request }) => {
          if (!isHTTPError(error)) {
            return error;
          }

          const status = error.response.status;
          const url = urlForCapture(request.url);
          const shouldCaptureException =
            status === StatusCodes.UNAUTHORIZED ||
            (status !== undefined && status >= StatusCodes.INTERNAL_SERVER_ERROR);

          if (shouldCaptureException) {
            captureModuleException({
              module: inferModuleFromUrl(url),
              source: 'setupZeus.hook',
              context: {
                method: error.options.method,
                url,
                status,
              },
              error: error.message,
            });
          }

          if (status === StatusCodes.UNAUTHORIZED) {
            const ref = zeusAuthRef?.current;

            if (ref) {
              await ref.loginWithRedirect({ returnTo: window.location.toString() });
            }
          }

          return error;
        },
      ],
    },
  };
}
